import axios from "axios";
import { isOfflineMode } from "@/helpers/offlineMode";

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_FLEX_GATEWAY_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// How long the request interceptor will wait for the phoenix token to arrive
// before giving up and letting the request proceed (which would 401).
const TOKEN_WAIT_TIMEOUT_MS = 10_000;

let phoenixToken: string | null = null;
let ssoToken: string | null = null;

// Resolves as soon as a phoenix token is set, so in-flight requests that mount
// before the async token exchange completes can wait instead of firing without it.
let resolveTokenReady: (() => void) | null = null;
let tokenReady: Promise<void> = new Promise((resolve) => {
  resolveTokenReady = resolve;
});

export const setPhoenixToken = (token: string | null) => {
  phoenixToken = token;
  if (token) {
    resolveTokenReady?.();
    resolveTokenReady = null;
  } else {
    // Token cleared — arm a fresh promise for the next exchange.
    tokenReady = new Promise((resolve) => {
      resolveTokenReady = resolve;
    });
  }
};

export const setSsoToken = (token: string | null) => {
  ssoToken = token;
}

export const getPhoenixToken = () => phoenixToken;
export const getSsoToken = () => ssoToken;

apiClient.interceptors.request.use(
  async (config) => {
    // When online, wait for the phoenix token to be exchanged before sending,
    // so early requests don't race the auth flow and 401.
    if (!phoenixToken && !isOfflineMode()) {
      await Promise.race([
        tokenReady,
        new Promise((resolve) => setTimeout(resolve, TOKEN_WAIT_TIMEOUT_MS)),
      ]);
    }
    if (phoenixToken) {
      config.headers.phoenixToken = phoenixToken;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default apiClient;
